import { useState } from 'react';
import { Send, Loader, ClipboardList } from 'lucide-react';
import { ParticipantTabletopState, participantService } from '../../../services/participant.service';

interface Props {
  sessionId: string;
  tabletop: ParticipantTabletopState;
  onSubmit: () => void;
}

function formatSimTime(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `T+${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// Submit a structured decision, or log a call to an external party (plan
// 6.3). Both are the same underlying event kind ('decision') distinguished
// by `type` — see tabletop-second-screen.service.ts's submitDecision.
export function DecisionsTab({ sessionId, tabletop, onSubmit }: Props) {
  const [type, setType] = useState<'decision' | 'call_log'>('decision');
  const [text, setText] = useState('');
  const [calledParty, setCalledParty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!tabletop.seat) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <p className="text-[var(--pt-text-dimmer)] text-sm text-center">Claim a seat in the Brief tab to log decisions.</p>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await participantService.submitTabletopDecision(sessionId, {
        text: text.trim(),
        type,
        calledParty: type === 'call_log' ? calledParty.trim() || undefined : undefined,
      });
      setText('');
      setCalledParty('');
      onSubmit();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 lg:px-0 lg:pt-0">
        {tabletop.decisions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3">
            <ClipboardList size={24} className="text-[var(--pt-text-dimmer)]" />
            <p className="text-[var(--pt-text-dimmer)] text-sm">Nothing logged yet</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 lg:grid lg:grid-cols-2 lg:gap-3">
            {tabletop.decisions.map((d, i) => (
              <div key={i} className="bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wide text-primary font-semibold">
                    {d.type === 'call_log' ? `Called ${d.calledParty || 'external party'}` : 'Decision'}
                  </span>
                  <span className="text-[var(--pt-text-dimmer)] text-[11px] font-mono">{formatSimTime(d.simTimeMs)}</span>
                </div>
                <p className="text-[var(--pt-text)] text-sm mt-1 leading-snug">{d.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-[var(--pt-border)] p-3 lg:px-0 lg:pb-0 flex flex-col gap-2 bg-[var(--pt-bg)]">
        <div className="flex rounded-xl border border-[var(--pt-border)] overflow-hidden">
          {(['decision', 'call_log'] as const).map(opt => (
            <button
              key={opt}
              onClick={() => setType(opt)}
              className={`flex-1 py-2 text-xs font-semibold transition-colors ${type === opt ? 'bg-primary text-[var(--pt-text)]' : 'text-[var(--pt-text-dim)]'}`}
            >
              {opt === 'decision' ? 'Decision' : 'Log a call'}
            </button>
          ))}
        </div>
        {type === 'call_log' && (
          <input
            value={calledParty}
            onChange={e => setCalledParty(e.target.value)}
            placeholder="Who did you call?"
            className="bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-xl px-3 py-2 text-sm text-[var(--pt-text)] placeholder:text-[var(--pt-text-dimmer)]"
          />
        )}
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div className="flex items-center gap-2">
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={type === 'call_log' ? 'What was discussed?' : 'What did you decide?'}
            className="flex-1 bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-xl px-3 py-2 text-sm text-[var(--pt-text)] placeholder:text-[var(--pt-text-dimmer)]"
          />
          <button
            onClick={handleSubmit}
            disabled={!text.trim() || submitting}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary disabled:opacity-40 flex-shrink-0"
          >
            {submitting ? <Loader size={16} className="animate-spin text-[var(--pt-text)]" /> : <Send size={16} className="text-[var(--pt-text)]" />}
          </button>
        </div>
      </div>
    </div>
  );
}
